import type { ContentType, PersistedMessage } from "./types";

export interface MaterializedMessage extends PersistedMessage {
  readonly edited: boolean;
  readonly retracted: boolean;
}

export interface ImagePayload {
  readonly uuid: string;
  readonly format: "jpeg" | "png" | "gif" | "webp" | "heic" | "avif" | "unknown";
  readonly original: {
    readonly objectKey?: string;
    readonly url?: string;
    readonly sizeBytes: number;
    readonly width: number;
    readonly height: number;
  };
}

export interface AudioPayload {
  readonly uuid: string;
  readonly source: { readonly objectKey?: string; readonly url?: string; readonly sizeBytes: number };
  readonly durationMs: number;
  readonly codec?: string;
  readonly waveform?: readonly number[];
}

export interface VideoPayload {
  readonly version: 1;
  readonly kind: "video";
  readonly uuid: string;
  readonly source: { readonly objectKey?: string; readonly url?: string; readonly sizeBytes: number };
  readonly durationMs: number;
  readonly width: number;
  readonly height: number;
  readonly codec?: string;
}

export interface FilePayload {
  readonly version: 1;
  readonly kind: "file";
  readonly uuid: string;
  readonly source: { readonly objectKey?: string; readonly url?: string; readonly sizeBytes: number };
  readonly fileName: string;
  readonly extension?: string;
}

export type MediaReferencePayload = VideoPayload | FilePayload;

/** Applies ordered edit/retract events while preserving the audit timeline. */
export function materializeMessages(messages: readonly PersistedMessage[]): readonly MaterializedMessage[] {
  const visible = new Map<string, MaterializedMessage>();
  const order: string[] = [];
  for (const message of [...messages].sort((left, right) => left.seq - right.seq)) {
    if (message.contentType === "edit" && message.targetMessageId !== undefined) {
      const target = visible.get(message.targetMessageId);
      if (target !== undefined && !target.retracted) {
        visible.set(message.targetMessageId, { ...target, payload: message.payload, edited: true });
      }
      continue;
    }
    if (message.contentType === "retract" && message.targetMessageId !== undefined) {
      const target = visible.get(message.targetMessageId);
      if (target !== undefined) visible.set(message.targetMessageId, { ...target, retracted: true });
      continue;
    }
    visible.set(message.messageId, { ...message, edited: false, retracted: false });
    order.push(message.messageId);
  }
  return order.flatMap((messageId) => {
    const message = visible.get(messageId);
    return message === undefined ? [] : [message];
  });
}

/** Returns true once the server echo has replaced a matching optimistic row. */
export function hasPersistedClientMessage(
  messages: readonly PersistedMessage[],
  clientMsgId: string,
): boolean {
  return messages.some((message) => message.clientMsgId === clientMsgId);
}

export function contentLabel(type: ContentType): string {
  const labels: Record<ContentType, string> = {
    text: "文本",
    image: "图片",
    audio: "语音",
    custom: "自定义",
    command: "CMD 指令",
    system: "系统消息",
    "media-ref": "媒体引用",
    ciphertext: "密文",
    edit: "编辑",
    retract: "撤回",
  };
  return labels[type];
}

export function parseJsonPayload<T>(
  payload: string,
  guard: (value: unknown) => value is T,
): T | null {
  try {
    const value: unknown = JSON.parse(payload);
    return guard(value) ? value : null;
  } catch {
    return null;
  }
}

export function isImagePayload(value: unknown): value is ImagePayload {
  if (!isRecord(value) || typeof value.uuid !== "string" || !isRecord(value.original)) {
    return false;
  }
  return typeof value.format === "string" &&
    typeof value.original.sizeBytes === "number" &&
    typeof value.original.width === "number" &&
    typeof value.original.height === "number" &&
    (typeof value.original.objectKey === "string" || typeof value.original.url === "string");
}

export function isAudioPayload(value: unknown): value is AudioPayload {
  if (!isRecord(value) || typeof value.uuid !== "string" || !isRecord(value.source)) {
    return false;
  }
  return typeof value.durationMs === "number" &&
    typeof value.source.sizeBytes === "number" &&
    (typeof value.source.objectKey === "string" || typeof value.source.url === "string");
}

export function isMediaReferencePayload(value: unknown): value is MediaReferencePayload {
  if (
    !isRecord(value) || value.version !== 1 || typeof value.uuid !== "string" ||
    !isRecord(value.source) || typeof value.source.sizeBytes !== "number" ||
    (typeof value.source.objectKey !== "string" && typeof value.source.url !== "string")
  ) return false;
  if (value.kind === "video") {
    return typeof value.durationMs === "number" &&
      typeof value.width === "number" && typeof value.height === "number";
  }
  return value.kind === "file" && typeof value.fileName === "string";
}

export function isJsonObject(value: unknown): value is Record<string, unknown> {
  return isRecord(value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function imageFormat(mimeType: string): ImagePayload["format"] {
  const value = mimeType.toLowerCase().split("/")[1];
  return value === "jpg" ? "jpeg" :
    value === "jpeg" || value === "png" || value === "gif" || value === "webp" ||
      value === "heic" || value === "avif"
      ? value
      : "unknown";
}
